/*
  BlackBoost product fitment check (snippets/bb-fitment.liquid).
  - Vehicle values come from product metafield custom.vehicle ("Make|Model|Version").
  - The chosen vehicle is remembered in the shopper's browser (localStorage "bbVehicle"), shared with the Vehicle selector.
*/
(() => {
  if (window.BBFitment) return;

  const KEY = 'bbVehicle';
  const PARAM = 'filter.p.m.custom.vehicle';
  const CACHE_MINUTES = 30;
  const CHANGE_EVENT = 'bb:vehicle-change';

  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || 'null');
      return v && Array.isArray(v.values) && v.values.length ? v : null;
    } catch (e) { return null; }
  };
  const write = (v) => {
    try { if (v) localStorage.setItem(KEY, JSON.stringify(v)); else localStorage.removeItem(KEY); } catch (e) {}
    window.dispatchEvent(new Event(CHANGE_EVENT));
  };
  const cacheGet = (key) => {
    try {
      const c = JSON.parse(sessionStorage.getItem(key) || 'null');
      return c && Date.now() - c.t < CACHE_MINUTES * 60000 ? c.rows : null;
    } catch (e) { return null; }
  };
  const cacheSet = (key, rows) => {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), rows })); } catch (e) {}
  };
  const parts = (s) => {
    const p = String(s).split('|');
    return { make: p[0] || '', model: p[1] || '', version: p.slice(2).join(' ') };
  };
  const nice = (values) => {
    if (!values.length) return '';
    const a = parts(values[0]);
    if (values.length === 1) return `${a.make} ${a.model}${a.version ? ' · ' + a.version : ''}`;
    const same = values.every((v) => { const b = parts(v); return b.make === a.make && b.model === a.model; });
    return same ? `${a.make} ${a.model} (all versions)` : `${a.make} ${a.model} +${values.length - 1}`;
  };
  const cmp = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

  /* Vehicle list, read from sections/bb-vehicle-data (250 products per page). */
  const trees = {};
  const fetchPage = async (url, number) => {
    const res = await fetch(`${url}&page=${number}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(String(res.status));
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const node = doc.querySelector('[data-bb-vehicles]');
    return {
      pages: node ? parseInt(node.dataset.pages || '1', 10) || 1 : 1,
      rows: node ? JSON.parse(node.textContent) : []
    };
  };
  const loadTree = (url) => {
    if (trees[url]) return trees[url];
    trees[url] = (async () => {
      const cacheKey = 'bbVeh:' + url;
      let rows = cacheGet(cacheKey);
      if (!rows) {
        const first = await fetchPage(url, 1);
        rows = first.rows;
        if (first.pages > 1) {
          const more = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => fetchPage(url, i + 2)));
          more.forEach((result) => { rows = rows.concat(result.rows); });
        }
        cacheSet(cacheKey, rows);
      }
      const tree = {};
      const values = new Set();
      rows.forEach((value) => {
        if (!value || values.has(value)) return;
        values.add(value);
        const p = parts(value);
        if (!p.make || !p.model) return;
        tree[p.make] = tree[p.make] || {};
        tree[p.make][p.model] = tree[p.make][p.model] || [];
        tree[p.make][p.model].push({ v: value, version: p.version });
      });
      return { tree, values };
    })();
    trees[url].catch(() => { delete trees[url]; });
    return trees[url];
  };

  /* Make > Model > Version picker inside a <dialog class="bb-veh__dialog">. */
  class Picker {
    constructor(dialog, options) {
      this.dialog = dialog;
      this.options = options;
      this.list = dialog.querySelector('[data-bb-list]');
      this.filter = dialog.querySelector('[data-bb-filter]');
      this.stepEl = dialog.querySelector('[data-bb-step]');
      this.crumbs = dialog.querySelector('[data-bb-crumbs]');
      this.statusEl = dialog.querySelector('[data-bb-status]');
      this.backBtn = dialog.querySelector('[data-bb-back]');
      dialog.querySelector('[data-bb-close]').addEventListener('click', () => dialog.close());
      this.backBtn.addEventListener('click', () => this.back());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
      this.filter.addEventListener('input', () => this.applyFilter());
    }

    status(message) {
      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
    }

    skeleton() {
      this.list.textContent = '';
      this.list.setAttribute('aria-busy', 'true');
      for (let i = 0; i < 9; i += 1) {
        const s = document.createElement('div');
        s.className = 'bb-veh__opt bb-veh__opt--skeleton';
        s.setAttribute('aria-hidden', 'true');
        this.list.append(s);
      }
    }

    async open(startAt) {
      this.make = null;
      this.model = null;
      this.step = 'make';
      if (!this.dialog.open) {
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      }
      if (!this.tree) {
        this.crumbs.hidden = true;
        this.backBtn.hidden = true;
        this.stepEl.textContent = 'Loading vehicles';
        this.status('');
        this.skeleton();
        try { this.tree = (await loadTree(this.options.url)).tree; } catch (e) {
          this.list.textContent = '';
          this.list.removeAttribute('aria-busy');
          this.stepEl.textContent = '';
          this.status('Could not load the vehicle list. Please check your connection and try again.');
          return;
        }
        this.list.removeAttribute('aria-busy');
      }
      const cur = this.options.current();
      if (cur.length) {
        const p = parts(cur[0]);
        if (this.tree[p.make]) {
          this.make = p.make;
          this.step = 'model';
          if (startAt === 'version' && this.tree[p.make][p.model] && this.tree[p.make][p.model].length > 1) {
            this.model = p.model;
            this.step = 'version';
          }
        }
      }
      this.show();
    }

    back() {
      this.step = this.step === 'version' ? 'model' : 'make';
      this.show();
    }

    choose(values, label) {
      this.options.onChoose(values, label);
    }

    show() {
      const tree = this.tree;
      const cur = this.options.current();
      const curParts = cur.length ? parts(cur[0]) : null;
      let items = [];
      let step = '';
      let crumbs = '';
      if (this.step === 'make') {
        step = 'Choose a make';
        items = Object.keys(tree).sort(cmp).map((make) => ({
          label: make,
          current: curParts && curParts.make === make,
          run: () => { this.make = make; this.step = 'model'; this.show(); }
        }));
      } else if (this.step === 'model') {
        step = 'Choose a model';
        crumbs = this.make;
        items = Object.keys(tree[this.make]).sort(cmp).map((model) => ({
          label: model,
          current: curParts && curParts.make === this.make && curParts.model === model,
          run: () => {
            const versions = tree[this.make][model];
            if (versions.length === 1) this.choose([versions[0].v], nice([versions[0].v]));
            else { this.model = model; this.step = 'version'; this.show(); }
          }
        }));
      } else {
        step = 'Choose your version';
        crumbs = `${this.make} › ${this.model}`;
        const versions = tree[this.make][this.model].slice().sort((a, b) => cmp(a.version, b.version));
        const allValues = versions.map((x) => x.v);
        const all = {
          label: `All ${this.model} versions`,
          all: true,
          current: cur.length > 1 && allValues.every((v) => cur.includes(v)),
          run: () => this.choose(allValues, `${this.make} ${this.model} (all versions)`)
        };
        items = (this.options.allowAll === false ? [] : [all]).concat(versions.map((x) => ({
          label: x.version || this.model,
          current: cur.length === 1 && cur[0] === x.v,
          run: () => this.choose([x.v], nice([x.v]))
        })));
      }
      this.stepEl.textContent = step;
      this.crumbs.textContent = crumbs;
      this.crumbs.hidden = !crumbs;
      this.backBtn.hidden = this.step === 'make';
      this.filter.value = '';
      this.list.textContent = '';
      items.forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bb-veh__opt' + (item.all ? ' bb-veh__opt--all' : '');
        button.textContent = item.label;
        button.dataset.label = item.label.toLowerCase();
        if (item.current) button.setAttribute('aria-current', 'true');
        button.addEventListener('click', item.run);
        this.list.append(button);
      });
      this.status(items.length ? '' : 'No vehicles found yet.');
      this.list.scrollTop = 0;
      if (window.matchMedia('(min-width: 768px)').matches) this.filter.focus();
    }

    applyFilter() {
      const query = this.filter.value.trim().toLowerCase();
      let shown = 0;
      this.list.querySelectorAll('.bb-veh__opt').forEach((button) => {
        const match = !query || button.dataset.label.includes(query);
        button.hidden = !match;
        if (match) shown += 1;
      });
      this.status(shown ? '' : 'No match. Try another spelling.');
    }
  }

  /* Fitment box on product pages. */
  class BbFitment extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.fits = JSON.parse(this.querySelector('[data-bb-fit-values]').textContent) || []; } catch (e) { this.fits = []; }
      this.eyebrow = this.querySelector('[data-bb-fit-eyebrow]');
      this.titleEl = this.querySelector('[data-bb-fit-title]');
      this.note = this.querySelector('[data-bb-fit-note]');
      this.cta = this.querySelector('[data-bb-fit-cta]');
      this.ctaText = this.querySelector('[data-bb-fit-cta-text]');
      this.checkBtn = this.querySelector('[data-bb-fit-check]');
      this.checkText = this.querySelector('[data-bb-fit-check-text]');
      this.changeBtn = this.querySelector('[data-bb-fit-change]');
      this.picker = new Picker(this.querySelector('dialog'), {
        url: this.dataset.url,
        current: () => { const s = read(); return s ? s.values : []; },
        onChoose: (values, label) => { write({ values, label }); this.picker.dialog.close(); }
      });
      this.checkBtn.addEventListener('click', () => this.picker.open(this.state === 'partial' ? 'version' : undefined));
      this.changeBtn.addEventListener('click', () => this.picker.open());
      const warm = () => { loadTree(this.dataset.url).catch(() => {}); };
      this.addEventListener('pointerenter', warm, { once: true });
      this.addEventListener('touchstart', warm, { once: true, passive: true });
      window.addEventListener(CHANGE_EVENT, () => this.render());
      window.addEventListener('storage', (e) => { if (e.key === KEY) this.render(); });
      this.render();
    }

    paint(state) {
      this.state = state.state;
      this.dataset.state = state.state;
      this.eyebrow.textContent = state.eyebrow;
      this.titleEl.textContent = '';
      if (state.name) {
        const strong = document.createElement('strong');
        strong.textContent = state.name;
        this.titleEl.append(state.lead + ' ', strong);
      } else {
        this.titleEl.textContent = state.lead;
      }
      this.note.textContent = state.note || '';
      this.note.hidden = !state.note;
      this.cta.hidden = !state.cta;
      if (state.cta) {
        this.ctaText.textContent = state.cta.label;
        this.cta.href = state.cta.href;
      }
      this.checkBtn.hidden = !state.check;
      if (state.check) this.checkText.textContent = state.check;
      this.changeBtn.hidden = !state.change;
    }

    render() {
      const saved = read();
      if (!saved) {
        this.paint({ state: 'none', eyebrow: 'Fitment check', lead: 'Will this fit your car?', note: 'Select your car to check before you buy.', check: 'Check fitment' });
        return;
      }
      const label = saved.label || nice(saved.values);
      const matched = saved.values.filter((v) => this.fits.includes(v));
      if (matched.length && matched.length === saved.values.length) {
        this.paint({ state: 'fits', eyebrow: 'Confirmed fit', lead: 'Fits your', name: label, change: true });
      } else if (matched.length) {
        const p = parts(saved.values[0]);
        const versions = matched.map((v) => parts(v).version).filter(Boolean).join(', ');
        this.paint({ state: 'partial', eyebrow: 'Check your version', lead: 'Fits some', name: `${p.make} ${p.model}`, note: `Fits: ${versions}. Choose your exact version to confirm.`, check: 'Choose your version', change: true });
      } else {
        const p = parts(saved.values[0]);
        const url = new URL(this.dataset.all, location.origin);
        saved.values.forEach((v) => url.searchParams.append(PARAM, v));
        const href = url.pathname + url.search;
        /* data-strict="true" only once custom.vehicle lists every car in the COMPATIBLE list. */
        if (this.dataset.strict === 'true') {
          this.paint({ state: 'nofit', eyebrow: 'Does not fit', lead: 'This part does not fit your', name: label, note: 'Here is what we list for it.', cta: { label: `See parts for your ${p.model}`, href }, change: true });
        } else {
          this.paint({ state: 'unlisted', eyebrow: 'Not listed for your car', lead: 'Not listed for your', name: label, note: 'Check the compatible list below, or see the parts we list for your car.', cta: { label: `See parts for your ${p.model}`, href }, change: true });
        }
        this.countFor(saved, p, href);
      }
    }

    async countFor(saved, p, href) {
      const key = saved.values.join('||');
      this.countKey = key;
      try {
        const url = new URL(this.dataset.countUrl, location.origin);
        saved.values.forEach((v) => url.searchParams.append(PARAM, v));
        const res = await fetch(url.pathname + url.search, { credentials: 'same-origin' });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const node = doc.querySelector('[data-bb-count]');
        const count = node ? parseInt(node.dataset.bbCount, 10) || 0 : null;
        if (this.countKey !== key || (this.state !== 'nofit' && this.state !== 'unlisted') || count === null) return;
        if (count > 0) {
          this.ctaText.textContent = `See the ${count} part${count === 1 ? '' : 's'} for your ${p.model}`;
        } else {
          this.note.textContent = this.state === 'nofit' ? 'We do not list parts for it yet. Message us and we will check.' : 'Check the compatible list below. Not sure? Message us and we will check.';
          this.ctaText.textContent = 'Message us';
          this.cta.href = this.dataset.help || href;
        }
      } catch (e) {}
    }
  }

  window.BBFitment = { read, write, nice, parts };
  if (!customElements.get('bb-fitment')) customElements.define('bb-fitment', BbFitment);
})();
